const React = require('react');
const { createElement: h, useEffect, useMemo, useRef, useState } = React;
const { getSystemObjectTheme } = require('../lib/systemObjectTheme');
const { request } = require('../lib/apiClient');
const dragData = require('../lib/dragData');
const NodeIcon = require('./NodeIcon');
const NodeParamsPanel = require('./NodeParamsPanel');
const ConfirmDialog = require('./ConfirmDialog');
require('./Sidebar.css');

function ChevronRightIcon() {
  return h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round' },
    h('polyline', { points: '9 18 15 12 9 6' })
  );
}

function ChevronLeftIcon() {
  return h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5, strokeLinecap: 'round', strokeLinejoin: 'round' },
    h('polyline', { points: '15 18 9 12 15 6' })
  );
}

function SearchIcon() {
  return h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
    h('circle', { cx: 11, cy: 11, r: 8 }),
    h('line', { x1: 21, y1: 21, x2: 16.65, y2: 16.65 })
  );
}

function CloseIcon() {
  return h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
    h('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
    h('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
  );
}

function UploadIcon() {
  return h('svg', { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    h('path', { d: 'M12 16V4' }),
    h('path', { d: 'M7 9l5-5 5 5' }),
    h('path', { d: 'M4 20h16' })
  );
}

function PlusIcon() {
  return h('svg', { width: 13, height: 13, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.5 },
    h('line', { x1: 12, y1: 5, x2: 12, y2: 19 }),
    h('line', { x1: 5, y1: 12, x2: 19, y2: 12 })
  );
}

function SectionChevronIcon(props) {
  var isOpen = !!props.isOpen;
  return h('svg', {
    width: 14,
    height: 14,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2.5,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    style: { transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.16s ease' }
  },
    h('polyline', { points: '9 18 15 12 9 6' })
  );
}

function LinkIcon() {
  return h('svg', { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    h('path', { d: 'M10 13a5 5 0 0 0 7.07 0l2.83-2.83a5 5 0 0 0-7.07-7.07L11 4' }),
    h('path', { d: 'M14 11a5 5 0 0 0-7.07 0L4.1 13.83a5 5 0 1 0 7.07 7.07L13 20' })
  );
}

function TrashIcon() {
  return h('svg', { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' },
    h('polyline', { points: '3 6 5 6 21 6' }),
    h('path', { d: 'M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2' }),
    h('path', { d: 'M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6' }),
    h('line', { x1: 10, y1: 11, x2: 10, y2: 17 }),
    h('line', { x1: 14, y1: 11, x2: 14, y2: 17 })
  );
}

function FileIcon() {
  return h('svg', { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' },
    h('path', { d: 'M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z' }),
    h('path', { d: 'M14 2v5h5' })
  );
}

function formatBytes(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1).replace(/\.0$/, '') + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, '') + ' MB';
}

function formatDateLabel(value) {
  if (!value) return '';
  var date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
}

function sanitizeUploadName(name) {
  return String(name || '')
    .replace(/\\/g, '/')
    .split('/')
    .pop()
    .replace(/[^\w.\-() ]/g, '_')
    .trim();
}

function readFileAsBase64(file) {
  return new Promise(function(resolve, reject) {
    var reader = new FileReader();
    reader.onload = function() {
      var result = String(reader.result || '');
      var commaIndex = result.indexOf(',');
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.onerror = function() {
      reject(new Error('Failed to read file.'));
    };
    reader.readAsDataURL(file);
  });
}

function copyText(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(text);
  }

  return Promise.reject(new Error('Clipboard is not available.'));
}

function FileRow(props) {
  var item = props.item;
  var onCopy = props.onCopy;
  var onDelete = props.onDelete;
  var pendingDelete = props.pendingDelete;
  var copiedPath = props.copiedPath;

  return h('div', { className: 'sb-file-row' },
    h('div', { className: 'sb-file-main' },
      h('div', { className: 'sb-file-icon' }, h(FileIcon)),
      h('div', { className: 'sb-file-info' },
        h('a', {
          className: 'sb-file-name',
          href: item.url || '#',
          target: item.url ? '_blank' : undefined,
          rel: item.url ? 'noreferrer' : undefined,
          onClick: item.url ? undefined : function(e) { e.preventDefault(); }
        }, item.name || item.path),
        h('div', { className: 'sb-file-meta' }, formatBytes(item.size) + ' • ' + formatDateLabel(item.updatedAt)),
        h('div', { className: 'sb-file-path' }, item.path)
      )
    ),
    h('div', { className: 'sb-file-actions' },
      h('button', {
        className: 'sb-file-action-btn',
        onClick: function() { onCopy(item); },
        disabled: !item.url,
        title: item.url ? 'Copy file URL' : 'No public URL available'
      }, copiedPath === item.path ? 'Copied' : h(LinkIcon)),
      h('button', {
        className: 'sb-file-delete-btn',
        onClick: function() { onDelete(item); },
        disabled: pendingDelete === item.path,
        title: 'Delete file'
      }, pendingDelete === item.path ? '...' : h(TrashIcon))
    )
  );
}

// Six-dot drag handle shown on the right of a system-object row on hover —
// signals the row is draggable onto the canvas.
function DragHandleIcon() {
  return h('svg', { width: 10, height: 16, viewBox: '0 0 10 16', fill: 'currentColor', 'aria-hidden': 'true' },
    h('circle', { cx: 2.5, cy: 3, r: 1.3 }),
    h('circle', { cx: 7.5, cy: 3, r: 1.3 }),
    h('circle', { cx: 2.5, cy: 8, r: 1.3 }),
    h('circle', { cx: 7.5, cy: 8, r: 1.3 }),
    h('circle', { cx: 2.5, cy: 13, r: 1.3 }),
    h('circle', { cx: 7.5, cy: 13, r: 1.3 })
  );
}

function TemplateCardIcon(props) {
  var item = props.item;
  if (item.logoUrl) {
    return h('img', {
      src: item.logoUrl,
      alt: item.title,
      className: 'sb-template-logo',
      onError: function(event) {
        event.target.style.display = 'none';
      }
    });
  }

  return h(NodeIcon, { type: item.icon || 'data', size: 18, strokeWidth: 1.5 });
}

// Detect touch as the PRIMARY input, not mere touch capability. Using
// maxTouchPoints > 0 mis-flagged touchscreen Windows laptops (which also have a
// mouse) as touch-only, disabling drag-and-drop. (pointer: coarse) is true only
// when the main pointer is a finger (phones/tablets), so laptops keep drag.
// Wrapped defensively: older browsers may lack matchMedia or the pointer media
// feature — in that case we fall back to NOT touch (drag stays enabled), which
// is the safe default for desktops.
function detectPrimaryTouch() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    var mq = window.matchMedia('(pointer: coarse)');
    // Unsupported media features report media === 'not all' in some browsers.
    if (mq && mq.media && mq.media.indexOf('not all') !== -1) return false;
    return !!(mq && mq.matches);
  } catch (err) {
    return false;
  }
}
var isTouchDevice = detectPrimaryTouch();

function TemplateCard(props) {
  var item = props.item;
  var onDelete = props.onDelete;
  var theme = getSystemObjectTheme(item && item.icon);
  // Sidebar tiles use the "tinted tile + colored glyph" look. We only pass the
  // accent (as --accent) + the glyph color inline; CSS picks the tint strength
  // per theme (stronger in light mode so it doesn't wash out). Logo tiles keep
  // their white plate via .sb-template-icon-has-logo.
  var iconStyle = (theme && theme.accent && !item.logoUrl)
    ? { '--accent': theme.accent, color: theme.accent }
    : null;
  var tooltip = item.subtitle || 'Tap or drag to add';

  var onTapAdd = props.onTapAdd;

  function addToCanvas() {
    window.dispatchEvent(new CustomEvent('add-template-to-canvas', { detail: { templateId: item.id } }));
    if (isTouchDevice && onTapAdd) onTapAdd();
  }

  return h('div', { className: 'sb-template-card-wrap' },
    h('button', {
      className: 'sb-template-card',
      title: tooltip,
      'aria-label': (item.title || item.label || 'Object') + (item.subtitle ? ' - ' + item.subtitle : ''),
      draggable: !isTouchDevice,
      onDragStart: isTouchDevice ? undefined : function(event) {
        dragData.setData(event.dataTransfer, dragData.TEMPLATE_TYPE, item.id);
        try { event.dataTransfer.effectAllowed = 'copy'; } catch (err) {}
      },
      onClick: isTouchDevice ? addToCanvas : undefined,
      onDoubleClick: isTouchDevice ? undefined : addToCanvas
    },
      h('div', { className: 'sb-template-icon' + (item.logoUrl ? ' sb-template-icon-has-logo' : ''), style: iconStyle }, h(TemplateCardIcon, { item: item })),
      h('div', { className: 'sb-template-info' },
        h('div', { className: 'sb-template-title' }, item.title || item.label || 'Object'),
        item.subtitle
          ? h('div', { className: 'sb-template-subtitle' }, item.subtitle)
          : null
      ),
    ),
    onDelete
      ? h('div', { className: 'sb-template-card-actions' },
          h('button', {
            className: 'sb-template-delete-btn',
            title: 'Delete',
            onClick: function() { onDelete(item); }
          }, h(TrashIcon))
        )
      : h('span', { className: 'sb-template-drag-handle', 'aria-hidden': 'true' }, h(DragHandleIcon))
  );
}

function Sidebar(props) {
  var objectTemplates = props.objectTemplates || [];
  var userTemplates = props.userTemplates || [];
  var onDeleteTemplate = props.onDeleteTemplate || null;
  var isRunning = props.isRunning || false;
  var selectedNode = props.selectedNode || null;
  var selectedNodeStatus = props.selectedNodeStatus || null;
  var selectedNodeCallCount = props.selectedNodeCallCount || 0;
  var sidebarVariableNodes = props.variableNodes || [];
  var browserActionNodes = props.browserActionNodes || [];
  var onNodeParamChange = props.onNodeParamChange || null;
  var onNodeParamRename = props.onNodeParamRename || null;
  var onNodeParamAdd = props.onNodeParamAdd || null;
  var onNodeParamRemove = props.onNodeParamRemove || null;
  var onNodeConditionRuleChange = props.onNodeConditionRuleChange || null;
  var onNodeCredentialChange = props.onNodeCredentialChange || null;
  var onCloseNodeParams = props.onCloseNodeParams || null;
  // Live (read-only) mode: the node params panel shows a notice, not the form.
  var nodeParamsReadOnly = !!props.readOnly;
  // Live mode "credentials only": render JUST the per-node account picker for the
  // selected node and hide the object/template/file palettes (those are staging
  // authoring tools). Account selection IS editable + savable here, the way
  // variable values are — everything else stays locked.
  var liveCredentialsOnly = !!props.liveCredentialsOnly;
  var showNodeParams = !!(selectedNode && onNodeParamChange);
  var [isOpen, setIsOpen] = useState(typeof window !== 'undefined' ? window.innerWidth > 640 : true);

  // When a node gets selected on the canvas, force the sidebar open so the
  // params panel is visible. Without this, a collapsed sidebar would hide the
  // editor and the user would think nothing happened.
  useEffect(function() {
    if (showNodeParams) setIsOpen(true);
  }, [showNodeParams]);
  var [files, setFiles] = useState([]);
  var [searchTerm, setSearchTerm] = useState('');
  var [loading, setLoading] = useState(false);
  var [uploading, setUploading] = useState(false);
  var [error, setError] = useState('');
  var [copiedPath, setCopiedPath] = useState('');
  var [pendingDelete, setPendingDelete] = useState('');
  var [confirmDeleteFile, setConfirmDeleteFile] = useState(null);
  var [isFilesOpen, setIsFilesOpen] = useState(true);
  var fileInputRef = useRef(null);
  var wasOpenBeforeRun = React.useRef(null);

  useEffect(function() {
    if (isRunning) {
      if (wasOpenBeforeRun.current === null) {
        wasOpenBeforeRun.current = isOpen;
      }
      setIsOpen(false);
    } else if (wasOpenBeforeRun.current !== null) {
      setIsOpen(wasOpenBeforeRun.current);
      wasOpenBeforeRun.current = null;
    }
  }, [isRunning]);

  function loadFiles() {
    setLoading(true);
    setError('');
    return request('/files')
      .then(function(response) {
        setFiles(response && response.data ? response.data : []);
      })
      .catch(function(err) {
        setError(err && err.message ? err.message : 'Failed to load files.');
      })
      .finally(function() {
        setLoading(false);
      });
  }

  useEffect(function() {
    loadFiles();
  }, []);

  function handleUploadClick() {
    if (uploading || !fileInputRef.current) return;
    fileInputRef.current.click();
  }

  function handleUploadChange(event) {
    var input = event.target;
    var file = input && input.files && input.files[0];
    if (!file) return;

    var safeName = sanitizeUploadName(file.name);
    if (!safeName) {
      setError('Invalid file name.');
      input.value = '';
      return;
    }

    setUploading(true);
    setError('');

    readFileAsBase64(file)
      .then(function(contentBase64) {
        return request('/files/upload', {
          method: 'POST',
          body: {
            path: safeName,
            contentBase64: contentBase64
          }
        });
      })
      .then(function() {
        return loadFiles();
      })
      .catch(function(err) {
        setError(err && err.message ? err.message : 'Upload failed.');
      })
      .finally(function() {
        setUploading(false);
        input.value = '';
      });
  }

  function handleCopyUrl(item) {
    if (!item || !item.url) return;
    copyText(item.url)
      .then(function() {
        setCopiedPath(item.path);
        setTimeout(function() {
          setCopiedPath(function(current) {
            return current === item.path ? '' : current;
          });
        }, 1600);
      })
      .catch(function() {
        setError('Failed to copy file URL.');
      });
  }

  function handleDeleteFile(item) {
    if (!item || !item.path || pendingDelete) return;
    setConfirmDeleteFile(item);
  }

  function performDeleteFile(item) {
    setPendingDelete(item.path);
    setError('');
    request('/files?path=' + encodeURIComponent(item.path), { method: 'DELETE' })
      .then(function() {
        setFiles(function(current) {
          return current.filter(function(fileItem) { return fileItem.path !== item.path; });
        });
      })
      .catch(function(err) {
        setError(err && err.message ? err.message : 'Delete failed.');
      })
      .finally(function() {
        setPendingDelete('');
      });
  }

  var filteredFiles = useMemo(function() {
    var query = String(searchTerm || '').trim().toLowerCase();
    if (!query) return files;
    return files.filter(function(item) {
      return item.name.toLowerCase().indexOf(query) >= 0 || item.path.toLowerCase().indexOf(query) >= 0;
    });
  }, [files, searchTerm]);

  var filteredObjectTemplates = useMemo(function() {
    var query = String(searchTerm || '').trim().toLowerCase();
    if (!query) return objectTemplates;
    return objectTemplates.filter(function(item) {
      var title = String(item.title || item.label || '').toLowerCase();
      var subtitle = String(item.subtitle || '').toLowerCase();
      return title.indexOf(query) >= 0 || subtitle.indexOf(query) >= 0;
    });
  }, [objectTemplates, searchTerm]);

  var filteredUserTemplates = useMemo(function() {
    var query = String(searchTerm || '').trim().toLowerCase();
    if (!query) return userTemplates;
    return userTemplates.filter(function(item) {
      var title = String(item.title || item.label || '').toLowerCase();
      var subtitle = String(item.subtitle || '').toLowerCase();
      return title.indexOf(query) >= 0 || subtitle.indexOf(query) >= 0;
    });
  }, [userTemplates, searchTerm]);

  return h(React.Fragment, null,
    !isOpen ? h('button', {
      className: 'sb-mobile-open-btn',
      onClick: function() { setIsOpen(true); },
      title: 'Open object panel'
    }, h(ChevronLeftIcon)) : null,
    confirmDeleteFile
      ? h(ConfirmDialog, {
          title: 'Delete file?',
          message: 'Delete "' + confirmDeleteFile.name + '". This cannot be undone.',
          confirmLabel: 'Delete',
          cancelLabel: 'Cancel',
          danger: true,
          onConfirm: function() {
            var item = confirmDeleteFile;
            setConfirmDeleteFile(null);
            performDeleteFile(item);
          },
          onCancel: function() { setConfirmDeleteFile(null); }
        })
      : null,
    h('aside', { className: 'sidebar' + (isOpen ? ' sidebar-open' : ' sidebar-collapsed') + (showNodeParams ? ' sidebar-wide' : '') },
    isOpen ? h('div', {
      className: 'sb-mobile-backdrop',
      onClick: function() { setIsOpen(false); }
    }) : null,
    h('button', {
      className: 'sb-toggle-tab',
      onClick: function() { setIsOpen(function(value) { return !value; }); },
      title: isOpen ? 'Collapse panel' : 'Open object selection'
    },
      h('span', { className: 'sb-toggle-tab-icon' }, h(ChevronRightIcon)),
      h('span', { className: 'sb-toggle-tab-label' }, 'Objects')
    ),
    h('div', { className: 'sb-inner' + (showNodeParams ? ' sb-inner-with-params' : '') },

      // Slide-in node params overlay. Renders above sb-content when a node is
      // selected on the canvas. Clicking the X (or deselecting on the canvas)
      // hides this and restores System Objects / Templates / Files.
      showNodeParams
        ? h('div', { className: 'sb-params-overlay' },
            h(NodeParamsPanel, {
              node: selectedNode,
              nodeStatus: selectedNodeStatus,
              nodeCallCount: selectedNodeCallCount,
              variableNodes: sidebarVariableNodes,
              browserActionNodes: browserActionNodes,
              onParamChange: onNodeParamChange,
              onParamRename: onNodeParamRename,
              onParamAdd: onNodeParamAdd,
              onParamRemove: onNodeParamRemove,
              onConditionRuleChange: onNodeConditionRuleChange || function() {},
              onCredentialChange: onNodeCredentialChange || function() {},
              onClose: onCloseNodeParams || function() {},
              readOnly: nodeParamsReadOnly,
              credentialsOnly: liveCredentialsOnly
            })
          )
        : null,

      // Live "credentials only" mode hides the authoring palettes entirely. When
      // no node is selected we show a short hint so the panel isn't blank.
      liveCredentialsOnly
        ? (showNodeParams ? null : h('div', { className: 'sb-content' },
            h('div', { className: 'sb-templates-empty' },
              'Select a node to choose which connected account it uses.')
          ))
        : h('div', { className: 'sb-content' },
        h('div', { className: 'sb-section' },
          h('div', { className: 'sb-section-header' },
            h('div', { className: 'sb-section-title' }, 'System Objects'),
            h('span', { className: 'sb-section-count' }, filteredObjectTemplates.length)
          ),
          filteredObjectTemplates.length > 0
            ? h('div', { className: 'sb-template-list' },
                filteredObjectTemplates.map(function(item) {
                  return h(TemplateCard, { key: item.id, item: item, onTapAdd: function() { setIsOpen(false); } });
                })
              )
            : h('div', { className: 'sb-templates-empty' }, 'No system objects match your search')
        ),
        h('div', { className: 'sb-section' },
          h('div', { className: 'sb-section-header' },
            h('div', { className: 'sb-section-title' }, 'Preset Nodes'),
            h('div', { className: 'sb-section-actions' },
              h('span', { className: 'sb-section-count' }, filteredUserTemplates.length),
              h('button', {
                className: 'sb-add-btn',
                type: 'button',
                title: 'Create preset nodes by saving a custom node from the canvas',
                disabled: true
              }, h(PlusIcon), 'Saved')
            )
          ),
          filteredUserTemplates.length > 0
            ? h('div', { className: 'sb-template-list' },
                filteredUserTemplates.map(function(item) {
                  // Built-in system templates (isSystemTemplate) are undeletable
                  // — no delete handler, so the card shows the drag handle
                  // instead of a trash button. User templates stay deletable.
                  var canDelete = onDeleteTemplate && !item.isSystemTemplate;
                  return h(TemplateCard, {
                    key: item.id,
                    item: item,
                    onDelete: canDelete ? function() { onDeleteTemplate(item.id); } : null,
                    onTapAdd: function() { setIsOpen(false); }
                  });
                })
              )
            : h('div', { className: 'sb-templates-empty' }, 'No preset nodes saved yet')
        ),
        h('div', { className: 'sb-section' },
          h('div', { className: 'sb-section-header' },
            h('div', { className: 'sb-section-title' }, 'Your Files'),
            h('div', { className: 'sb-section-actions' },
              h('span', { className: 'sb-section-count' }, files.length),
              h('button', {
                className: 'sb-collapse-btn',
                type: 'button',
                title: isFilesOpen ? 'Collapse files' : 'Expand files',
                onClick: function() { setIsFilesOpen(function(value) { return !value; }); }
              }, h(SectionChevronIcon, { isOpen: isFilesOpen })),
              h('button', {
                className: 'sb-add-btn',
                onClick: handleUploadClick,
                disabled: uploading || !isFilesOpen
              }, h(UploadIcon), uploading ? 'Uploading...' : 'Upload')
            )
          ),
          h('input', {
            ref: fileInputRef,
            type: 'file',
            className: 'sb-file-input',
            onChange: handleUploadChange
          }),
          isFilesOpen
            ? [
                error ? h('div', { key: 'files-error', className: 'sb-file-feedback sb-file-feedback-error' }, error) : null,
                loading
                  ? h('div', { key: 'files-loading', className: 'sb-templates-empty' }, 'Loading files...')
                  : filteredFiles.length > 0
                    ? h('div', { key: 'files-list', className: 'sb-file-list' },
                        filteredFiles.map(function(item) {
                          return h(FileRow, {
                            key: item.path,
                            item: item,
                            onCopy: handleCopyUrl,
                            onDelete: handleDeleteFile,
                            pendingDelete: pendingDelete,
                            copiedPath: copiedPath
                          });
                        })
                      )
                    : h('div', { key: 'files-empty', className: 'sb-templates-empty' }, files.length > 0 ? 'No files match your search' : 'No uploaded files yet')
              ]
            : h('div', { className: 'sb-templates-empty' }, 'File manager collapsed')
        )
      )
    )
  ));
}

module.exports = Sidebar;
